import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import classnames from "classnames";
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KlarnaMark from '../general/KlarnaMark'
import { rootScopeSelector } from '../../selectors/scope'

const PREFIX = 'Percentage';
const classes = {
  percentage: `${PREFIX}-percentage`,
  chip: `${PREFIX}-chip`,
  pair: `${PREFIX}-pair`,
  klarnaDisc: `${PREFIX}-klarnaDisc`,
  ring: `${PREFIX}-ring`,
  value: `${PREFIX}-value`,
  text: `${PREFIX}-text`,
  title: `${PREFIX}-title`,
  sub: `${PREFIX}-sub`,
};

// Slagingspercentage. Pas hier het getal aan als het wijzigt.
const PASS_RATE = 93;
const PASS_TITLE = 'Slagingspercentage';
const PASS_SUB = 'van onze studenten haalt het tentamen';

const RADIUS = 27;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const Percentage = styled(({ className, ...props }) => {
  const { t } = useTranslation();
  const rootScope = useSelector(rootScopeSelector);
  // De Klarna-pil hoort bij athenastudies.nl. De andere sites op deze codebase
  // (DE, UK, AthenaSchool, ...) houden de losse slagingspercentage-pil.
  const showKlarna = rootScope?.abbreviation === 'ASTU';

  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1400;
            let start = null;
            const step = (ts) => {
              if (start === null) start = ts;
              const p = Math.min((ts - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(PASS_RATE * eased);
              if (p < 1) requestAnimationFrame(step);
              else setValue(PASS_RATE);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const offset = CIRCUMFERENCE * (1 - value / 100);

  return (
    <Box
      ref={ref}
      className={classnames(className, classes.percentage, { [classes.pair]: showKlarna })}
      {...props}
    >
      <div className={classes.chip}>
        <div className={classes.ring}>
          <svg width="64" height="64" viewBox="0 0 64 64" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="32" cy="32" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="6" />
            <circle
              cx="32" cy="32" r={RADIUS} fill="none" stroke="#22A565" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE} strokeDashoffset={offset}
            />
          </svg>
          <span className={classes.value}>{Math.round(value)}%</span>
        </div>
        <div className={classes.text}>
          <span className={classes.title}>{PASS_TITLE}</span>
          <span className={classes.sub}>{PASS_SUB}</span>
        </div>
      </div>
      {showKlarna && (
        <div className={classes.chip}>
          <div className={classes.klarnaDisc}>
            <KlarnaMark width="44px" />
          </div>
          <div className={classes.text}>
            <span className={classes.title}>
              {t('home.klarna.title', 'Betaal later of in 3 delen')}
            </span>
            <span className={classes.sub}>
              {t('home.klarna.sub', 'binnen 30 dagen of in 3 termijnen')}
            </span>
          </div>
        </div>
      )}
    </Box>
  );
})(({ theme }) => ({
  [`&.${classes.percentage}`]: {
    display: 'flex',
    justifyContent: 'center',
    margin: '36px 0 -6px',

    [`& .${classes.chip}`]: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 24px 12px 14px',
      borderRadius: '999px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.18)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
    },

    [`&.${classes.pair}`]: {
      gap: '16px',
      alignItems: 'stretch',

      [`& .${classes.chip}`]: {
        flex: '1 1 0',
        minWidth: 0,
      },
    },

    [`& .${classes.klarnaDisc}`]: {
      width: '64px',
      height: '64px',
      flex: '0 0 auto',
      borderRadius: '50%',
      background: '#FFA8CD',
      color: '#0B051D',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    [`& .${classes.ring}`]: {
      position: 'relative',
      width: '64px',
      height: '64px',
      flex: '0 0 auto',

      [`& .${classes.value}`]: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        fontSize: '17px',
        fontWeight: 800,
      },
    },

    [`& .${classes.text}`]: {
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'left',

      [`& .${classes.title}`]: {
        color: theme.palette.common.white,
        fontSize: '15px',
        fontWeight: 700,
        lineHeight: 1.1,
      },
      [`& .${classes.sub}`]: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '12px',
        marginTop: '3px',
      },
    },

    [theme.breakpoints.down('sm')]: {
      margin: '26px 0 -4px',
      flexDirection: 'column',
      alignItems: 'stretch',
      [`& .${classes.chip}`]: {
        gap: '12px',
        padding: '10px 18px 10px 12px',
      },
      [`& .${classes.sub}`]: {
        display: 'none',
      },
    },
  },
}));

Percentage.propTypes = {
  children: PropTypes.node,
};

export default Percentage
